import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileUp, Loader2, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CONTACT } from "@/constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 3;
const MAX_SIZE = 8 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

export const VehicleDocForm = ({ kind, kindLabel }) => {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const valid = [];
    for (const f of incoming) {
      if (!ACCEPTED.includes(f.type)) {
        toast.error("Format non accepté", { description: `${f.name} : utilisez jpg, png, webp ou pdf.` });
        continue;
      }
      if (f.size > MAX_SIZE) {
        toast.error("Fichier trop volumineux", { description: `${f.name} dépasse 8 Mo.` });
        continue;
      }
      valid.push(f);
    }
    const merged = [...files, ...valid].slice(0, MAX_FILES);
    if (files.length + valid.length > MAX_FILES) {
      toast.error("3 documents maximum", { description: "Seuls les 3 premiers fichiers sont conservés." });
    }
    setFiles(merged);
  };

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Consentement requis", {
        description: "Veuillez accepter l'utilisation de vos données pour traiter votre demande.",
      });
      return;
    }
    setSending(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload-document`, fd);
        paths.push(res.data.path);
      }
      await axios.post(`${API}/vehicule-request`, {
        kind,
        ...form,
        documents: paths,
        consent,
      });
      const waText = [
        `Bonjour MECA AUTO CLEAN 01, je fais une demande : ${kindLabel}.`,
        `Nom : ${form.name}`,
        `Téléphone : ${form.phone}`,
        `E-mail : ${form.email}`,
        form.message ? `Message : ${form.message}` : null,
        paths.length ? `Documents joints : ${paths.length} (envoyés au garage par e-mail)` : null,
      ]
        .filter(Boolean)
        .join("\n");
      window.open(
        `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(waText)}`,
        "_blank",
      );
      toast.success("Demande envoyée", {
        description:
          "Votre demande et vos documents ont été transmis au garage par e-mail. WhatsApp s'ouvre aussi pour prévenir le garage.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
      setFiles([]);
      setConsent(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error("Envoi impossible", {
        description:
          err.response?.data?.detail ||
          `Réessayez dans un instant ou appelez le garage au ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid={`${kind}-form`} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          required
          placeholder="Votre nom *"
          value={form.name}
          onChange={set("name")}
          data-testid={`${kind}-name-input`}
          aria-label="Votre nom"
        />
        <Input
          required
          type="tel"
          placeholder="Votre téléphone *"
          value={form.phone}
          onChange={set("phone")}
          data-testid={`${kind}-phone-input`}
          aria-label="Votre téléphone"
        />
      </div>
      <Input
        required
        type="email"
        placeholder="Votre e-mail *"
        value={form.email}
        onChange={set("email")}
        data-testid={`${kind}-email-input`}
        aria-label="Votre e-mail"
      />
      <Textarea
        rows={3}
        placeholder="Votre message (dates souhaitées, durée, précisions...)"
        value={form.message}
        onChange={set("message")}
        data-testid={`${kind}-message-input`}
        aria-label="Votre message"
      />

      <div className="rounded-md border border-dashed border-border bg-secondary/30 p-4">
        <label
          htmlFor={`${kind}-file-input`}
          className="flex cursor-pointer items-center gap-3 text-sm"
          data-testid={`${kind}-upload-zone`}
        >
          <FileUp className="h-5 w-5 shrink-0 text-primary" />
          <span>
            <span className="block font-semibold text-foreground">
              Joindre vos documents (permis, carte d'identité, justificatif de domicile)
            </span>
            <span className="text-muted-foreground">
              jpg, png, webp ou pdf — 8 Mo maximum par fichier, 3 documents maximum
            </span>
          </span>
        </label>
        <input
          id={`${kind}-file-input`}
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          onChange={(e) => addFiles(e.target.files)}
          data-testid={`${kind}-file-input`}
          className="sr-only"
        />
        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid={`${kind}-file-list`}>
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  data-testid={`${kind}-file-remove-${i}`}
                  aria-label={`Retirer ${f.name}`}
                  className="text-muted-foreground hover:text-primary"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          data-testid={`${kind}-consent-checkbox`}
          aria-label="Consentement au traitement des données"
          className="mt-0.5"
        />
        <span>
          J'accepte que mes données et mes documents soient utilisés pour traiter
          ma demande, conformément à la{" "}
          <Link
            to="/politique-de-confidentialite"
            data-testid={`${kind}-privacy-link`}
            className="text-primary underline-offset-4 hover:underline"
          >
            politique de confidentialité
          </Link>
          . *
        </span>
      </label>
      <Button type="submit" disabled={sending} data-testid={`${kind}-submit-button`} className="w-full sm:w-auto">
        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Envoyer ma demande
      </Button>
    </form>
  );
};
